import { signInWithPopup, AuthProvider, User } from "firebase/auth";
import { doc, setDoc } from 'firebase/firestore';
import { auth } from "../config/firebaseConfig";  // Adjust this path according to your configuration
import { CreateAccountApi, VerifyAccountApi } from "../apis/authServices";
import { getUserDetails } from "../apis/homeServices";
import { useUserStore } from "../store/userStore";
import { isAdminRole } from "./roles";
/**
 * Reusable function to sign in with a specified provider.
 * @param provider - The authentication provider for(Google, Apple, etc.)
 * * @param setIsAuthenticated - Function to update authentication state
 * @param navigate - Function to redirect to a specified path
 * @returns User details or error message
 */

  
export const signInWithProvider = async (
  provider: AuthProvider,
  setIsAuthenticated: (auth: boolean) => void,
  navigate: (path: string) => void,
  isSignup: boolean // New parameter to distinguish sign-up vs. sign-in
  ) => {

  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
// Check if user is new (sign-up)
const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;

if (isSignup) {
  // Sign-up flow: Skip VerifyAccountApi, save new user data
  if (isNewUser) {
    await CreateAccountApi();
    navigate('/createAccount'); // Redirect to profile completion for username
  } else {
    navigate('/search'); // Existing user, treat as sign-in
  }
} else  {
    // Extract email
    const email = user.email;
    if (!email) {
      return "Error: Unable to retrieve email from provider.";
    }

    // Verify account status with VerifyAccountApi
    interface VerifyResponse {
      statusCode: number;
      message: string;
      statusId: number;
      statusName: string;
    }

    const verifyResponse = await VerifyAccountApi(email);

    const statusId = verifyResponse?.statusId ?? null;

    switch (statusId) {
      case 1: // Inactive → Redirect to Create Account
        navigate("/createAccount");
        return user; // Return user, as redirect is handled

      case 2: // Active → Proceed with authentication
       {
        setIsAuthenticated(true);
        
        // Fetch user details to get role and navigate accordingly
        try {
          const userDetails: any = await getUserDetails();

          // Store user details in userStore
          useUserStore.getState().setUsername(userDetails.userName);
          useUserStore.getState().setEmail(userDetails.email);
          useUserStore.getState().setUserRole(userDetails.role);
          useUserStore.getState().setReferralCode(userDetails.referralCode);
          
          // Navigate based on role (community users go to the user side)
          if (isAdminRole(userDetails.role)) {
            navigate("/admin/dashboard");
          } else {
            navigate("/search");
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
          // Fallback to /search if user details fetch fails
          navigate("/search");
        }
        
        return user;
    }
      case 3: // Blocked
      case 4: // Deleted
        return `Error: ${verifyResponse.message}`;

      case null: // Email does not exist
        await CreateAccountApi();
        navigate("/createAccount");
        return "Error: This email does not belong to an account. Please use an existing email or sign up.";

      default:
        return "Error: Unexpected response from the server. Please try again.";

    }
  }
    
    
    
    
  } catch (error: any) {
    console.error("Error during sign-in:", error?.code, error?.message);

    // User intentionally closed/cancelled the sign-in popup — not a real error,
    // so don't surface anything to them. Returning null skips the error UI.
    if (
      error?.code === "auth/popup-closed-by-user" ||
      error?.code === "auth/cancelled-popup-request" ||
      error?.code === "auth/user-cancelled"
    ) {
      return null;
    }

    // Map the remaining Firebase auth codes to user-friendly messages.
    const friendlyMessages: Record<string, string> = {
      "auth/popup-blocked":
        "Your browser blocked the sign-in popup. Please allow popups and try again.",
      "auth/account-exists-with-different-credential":
        "An account already exists with this email using a different sign-in method.",
      "auth/network-request-failed":
        "Network error. Please check your connection and try again.",
      "auth/too-many-requests":
        "Too many attempts. Please wait a moment and try again.",
    };

    return friendlyMessages[error?.code] || "Sign-in failed. Please try again.";
  }
};
